/* eslint-disable react-hooks/exhaustive-deps */
import {formatSecondsToMinSec, handleError} from "helpers"
import {KlassappTableHOC} from "HOC"
import {useModel, useMutation} from "hooks"
import {useCallback, useEffect} from "react"
import {RecordingPlaybackProvider} from "context/RecordingPlaybackContext"
import {RecordingPlayback} from "../RecordingPlayback"
import {studentCallLogService} from "services"
import {KlassappTableProps} from "types/common"
import {BaseDepartmentId} from "types/departments"
import {FilterKey, FilterList} from "types/filter"
import {KlassappTable, KlassappTableHeader} from "uiKit"
import moment from "moment"
import styles from "./StudentVoiceMailListView.module.css"
import cx from "classnames"
import {ReactComponent as VoiceMailReadCheckIcon} from "./assets/voiceMailReadCheck.svg"
import {StudentVoiceMail} from "types/student-voice-mail"

type VoiceMailTableRow = StudentVoiceMail & {date: string; time: string; callLength: string}

type Props = KlassappTableProps & {
    filterKey?: FilterKey
    filterParams?: {
        filters: FilterList
        search?: string
    }
    departmentId?: BaseDepartmentId
    studentProfileId?: number
}

function StudentVoiceMailList(props: Props) {
    const model = useModel()
    const {
        data,
        page,
        total,
        pageSize,
        columns,
        fields,
        allFields,
        isLoading,
        orderField,
        isLoadedTableFuncs,
        dispatch,
        dispatchFunc
    } = props
    const {filterKey, filterParams, departmentId, onUpdateRowData} = props

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getFilterMemoryKey", func: getFilterKey},
            {key: "getListData", func: getData},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
    }, [])

    useEffect(() => {
        if (isLoadedTableFuncs) {
            getData()
        }
    }, [isLoadedTableFuncs, page, pageSize, orderField?.field, orderField?.order, filterParams])

    const getFilterKey = useCallback(() => {
        return filterKey
    }, [filterKey])

    const markVoiceMailAsRead = useMutation(
        async (payload: {id: number; row: VoiceMailTableRow}) => {
            await studentCallLogService.markVoiceMailAsRead({id: payload.id})
        },
        {
            onSuccess: (_void, variables) => {
                onUpdateRowData?.({
                    ...variables.row,
                    readAt: new Date().toISOString(),
                    status: "read"
                })
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const getData = useCallback(async () => {
        try {
            dispatch({isLoading: true})

            const storageData = model.getStorageFilter(filterKey)
            let orderFieldData = orderField
            if (storageData?.colOrd) {
                const columns: any[] = getColumns()
                const columnOrder = columns.find((column: any) => column.columnIndex === storageData.colOrd.idx)
                orderFieldData = {field: columnOrder?.orderField || columnOrder?.field, order: storageData.colOrd.order}
            }

            const payload = {
                range: {
                    pageSize: pageSize,
                    page: page
                },
                departmentId,
                ...filterParams,
                sort: orderFieldData?.field
                    ? {
                          orderBy: orderFieldData.field,
                          orderDir: orderFieldData.order
                      }
                    : undefined
            }
            const {data, total} = await studentCallLogService.listVoiceMail(payload)
            const formattedDocuments = data.map((item: StudentVoiceMail) => {
                return {
                    ...item,
                    date: moment.utc(item.createdAt).local().format(model.getUserDateFormat()),
                    time: moment.utc(item.createdAt).local().format(model.getUserTimeFormat()),
                    callLength: formatSecondsToMinSec(item.duration)
                }
            })
            dispatch({data: formattedDocuments, total})
        } catch (err) {
            handleError(err)
        } finally {
            dispatch({isLoading: false})
        }
    }, [filterKey, filterParams, page, pageSize, orderField])

    const getFields = () => {
        const fields = ["Date", "Time", "Student ID", "Student Name", "Phone", "Call Length", "Voice Mail"]
        return fields
    }

    const getColumns = () => {
        const cols: any = [
            {
                title: "Date",
                field: "date",
                columnIndex: 1
            },
            {
                title: "Time",
                field: "time",
                columnIndex: 2
            },
            {
                title: "Student ID",
                field: "customProfileId",
                columnIndex: 3
            },
            {
                title: "Student Name",
                field: "studentFullName",
                columnIndex: 4
            },
            {
                title: "Phone",
                field: "callerPhoneNumber",
                columnIndex: 5
            },
            {
                title: "Call Length",
                field: "callLength",
                columnIndex: 6,
                render: (callLength: string, row: VoiceMailTableRow) => (
                    <div className={styles.callLengthCell}>
                        <span
                            className={cx(styles.readCheckIcon, {
                                [styles.readCheckRead]: !!row.readAt
                            })}
                            aria-hidden>
                            <VoiceMailReadCheckIcon />
                        </span>
                        <span className={cx({[styles.callLengthUnread]: !row.readAt})}>{callLength}</span>
                    </div>
                )
            },
            {
                title: "Voice Mail",
                field: "recordingUrl",
                columnIndex: 7,
                render: (recordingUrl: string | undefined, row: VoiceMailTableRow) => {
                    if (!recordingUrl) {
                        return null
                    }
                    return (
                        <RecordingPlayback
                            url={recordingUrl}
                            onBeforeToggle={() => {
                                if (!row.readAt) {
                                    markVoiceMailAsRead.mutate({id: row.id, row})
                                }
                            }}
                        />
                    )
                }
            }
        ]

        return cols
    }

    return (
        <RecordingPlaybackProvider>
            <KlassappTableHeader
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={props.onChangePage}
                onChangeRowPerPage={props.onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={props.onChangeFields}
                onDraggableColumn={props.onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                orderField={orderField}
                onChangeFields={props.onChangeFields}
                onUpdateRowData={props.onUpdateRowData}
                onUpdateTableData={props.onUpdateTableData}
                onClickSortColumn={props.onClickSortColumn}
                onDraggableColumn={props.onDraggableColumn}
            />
        </RecordingPlaybackProvider>
    )
}

export const StudentVoiceMailListView = KlassappTableHOC(StudentVoiceMailList)
